import { useEffect, useState } from "react";
import { Crown, Loader2, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { formatCoins } from "../lib/format";
import { playReward } from "../lib/sound";

export default function VipCard({ onReward }) {
  const { setCoins } = useAuth();
  const { lang } = useLang();
  const L = (hi, en) => (lang === "hi" ? hi : en);
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/vip/status").then(({ data }) => setV(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!v || !v.enabled) return null;

  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/vip/claim");
      setCoins(data.coins); playReward(); onReward?.();
      toast.success(`VIP +${formatCoins(data.reward)}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cur = v.levels[v.level_index];
  const pct = v.next_at ? Math.min(100, ((v.wagered - cur.min_wagered) / (v.next_at - cur.min_wagered)) * 100) : 100;

  return (
    <section className="mw-card p-6 relative overflow-hidden" data-testid="vip-card">
      <div className="absolute -right-6 -top-6 opacity-10"><Crown size={130} className="text-amber-400" /></div>
      <div className="relative z-10 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-amber-400/90">{L("आपका VIP लेवल", "Your VIP level")}</p>
            <h2 className="font-display text-3xl font-extrabold gold-text flex items-center gap-2" data-testid="vip-level"><Crown size={26} className="text-amber-400" /> {v.level}</h2>
          </div>
          {v.claimable > 0 && (
            <button onClick={claim} disabled={busy} data-testid="vip-claim-btn" className="btn-gold rounded-xl px-5 py-2.5 flex items-center gap-2 animate-pulse">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Crown size={16} />} {L("इनाम लें", "Claim")} {formatCoins(v.claimable)}
            </button>
          )}
        </div>
        <div>
          <div className="h-2.5 rounded-full bg-black/40 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-200 transition-[width] duration-700" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-1.5 text-xs text-slate-400" data-testid="vip-progress">
            {v.next ? <>{L("अगला लेवल", "Next")} <b className="text-amber-200">{v.next}</b>: {formatCoins(v.wagered)} / {formatCoins(v.next_at)} {L("कुल दांव", "wagered")}</> : L("आप सबसे ऊँचे लेवल पर हैं!", "You are at the top level!")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {v.levels.map((lv, i) => (
            <span key={i} className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs ${i <= v.level_index ? "border-amber-400/50 bg-amber-500/10 text-amber-200" : "border-white/10 text-slate-500"}`}>
              {i <= v.level_index ? (lv.claimed || !lv.reward ? <Check size={11} /> : <Crown size={11} />) : <Lock size={11} />}
              {lv.name}{lv.reward > 0 && <span className="font-mono-num"> · {formatCoins(lv.reward)}</span>}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
